// Executive Discovery System — 12-section intake for AI Growth Infrastructure Blueprint
// Each section captures operational context for generating transformation blueprints

export const DISCOVERY_SECTIONS = [
  {
    id: "company_overview",
    title: "Company Overview",
    description: "Tell us about your business so we can tailor your blueprint.",
    questions: [
      { id: "business_model", text: "How would you describe your primary business model?", type: "radio", options: [
        { value: "1", label: "Service-based (consulting, agency, professional services)" },
        { value: "2", label: "Product-based (physical or digital products)" },
        { value: "3", label: "SaaS / subscription" },
        { value: "4", label: "Marketplace / platform" },
        { value: "5", label: "Hybrid (services + product)" }
      ]},
      { id: "years_operating", text: "How long has the business been operating?", type: "radio", options: [
        { value: "1", label: "Less than 1 year" }, { value: "2", label: "1-3 years" },
        { value: "3", label: "3-5 years" }, { value: "4", label: "5-10 years" }, { value: "5", label: "10+ years" }
      ]},
      { id: "growth_stage", text: "Which best describes your current growth stage?", type: "radio", options: [
        { value: "1", label: "Survival — fighting to stay afloat" },
        { value: "2", label: "Stabilizing — consistent but flat" },
        { value: "3", label: "Growing — scaling but hitting walls" },
        { value: "4", label: "Scaling — systems struggling to keep up" },
        { value: "5", label: "Optimizing — growth is solid, seeking efficiency" }
      ]},
    ]
  },
  {
    id: "revenue_model",
    title: "Revenue Model",
    description: "How your business generates and retains revenue.",
    questions: [
      { id: "revenue_concentration", text: "How concentrated is your revenue across clients?", type: "radio", options: [
        { value: "1", label: "Top 3 clients = 60%+ of revenue" },
        { value: "2", label: "Top 5 clients = 50%+ of revenue" },
        { value: "3", label: "Moderately diversified" },
        { value: "4", label: "Well diversified across 20+ clients" },
        { value: "5", label: "Highly diversified, no client > 10%" }
      ]},
      { id: "revenue_predictability", text: "How predictable is your monthly revenue?", type: "scale", labels: ["Very unpredictable", "Mostly variable", "Somewhat predictable", "Mostly recurring", "Highly predictable/recurring"] },
      { id: "avg_deal_size", text: "What is your average deal size or customer value?", type: "radio", options: [
        { value: "1", label: "Under $1,000" }, { value: "2", label: "$1,000 - $5,000" },
        { value: "3", label: "$5,000 - $25,000" }, { value: "4", label: "$25,000 - $100,000" }, { value: "5", label: "$100,000+" }
      ]},
      { id: "pricing_strategy", text: "How sophisticated is your pricing strategy?", type: "scale", labels: ["No strategy — gut feel", "Basic cost-plus", "Market-based", "Value-based with tiers", "Dynamic/AI-optimized"] },
    ]
  },
  {
    id: "sales_process",
    title: "Sales Process",
    description: "How you find, qualify, and close deals.",
    questions: [
      { id: "lead_gen_consistency", text: "How consistent is your lead generation?", type: "scale", labels: ["Very inconsistent", "Inconsistent", "Moderate", "Consistent", "Very consistent"] },
      { id: "speed_to_lead", text: "How quickly does your team respond to inbound leads?", type: "radio", options: [
        { value: "1", label: "Hours or more" }, { value: "2", label: "30-60 minutes" },
        { value: "3", label: "15-30 minutes" }, { value: "4", label: "5-15 minutes" }, { value: "5", label: "Under 5 minutes (automated)" }
      ]},
      { id: "sales_process_structure", text: "How structured is your sales process?", type: "radio", options: [
        { value: "1", label: "No defined process — ad hoc" },
        { value: "2", label: "Basic steps but inconsistently followed" },
        { value: "3", label: "Documented playbook" },
        { value: "4", label: "Optimized with data and reporting" },
        { value: "5", label: "AI-augmented with automated workflows" }
      ]},
      { id: "lead_qualification", text: "How do you qualify leads?", type: "radio", options: [
        { value: "1", label: "No qualification — everyone gets treated the same" },
        { value: "2", label: "Manual gut-check by sales rep" },
        { value: "3", label: "Basic scoring criteria" },
        { value: "4", label: "Multi-factor scoring model" },
        { value: "5", label: "AI/ML-powered lead scoring" }
      ]},
      { id: "followup_consistency", text: "How consistent is your follow-up process?", type: "scale", labels: ["No follow-up system", "Sporadic", "Semi-consistent", "Systematic", "Fully automated + human"] },
      { id: "pipeline_visibility", text: "How much visibility do you have into your sales pipeline?", type: "scale", labels: ["No visibility", "Basic spreadsheet", "CRM with gaps", "Good CRM visibility", "Real-time dashboard with forecasting"] },
    ]
  },
  {
    id: "marketing_process",
    title: "Marketing Process",
    description: "How you attract attention and generate demand.",
    questions: [
      { id: "marketing_channels", text: "How many active marketing channels are you running?", type: "radio", options: [
        { value: "1", label: "None — rely on referrals/word of mouth" },
        { value: "2", label: "1-2 channels (basic)" },
        { value: "3", label: "3-4 channels" },
        { value: "4", label: "5+ channels, coordinated" },
        { value: "5", label: "Omnichannel with attribution tracking" }
      ]},
      { id: "content_production", text: "How much content does your business produce?", type: "radio", options: [
        { value: "1", label: "Almost none" }, { value: "2", label: "Occasional posts/emails" },
        { value: "3", label: "Weekly content" }, { value: "4", label: "Daily content across channels" },
        { value: "5", label: "AI-assisted content engine running daily" }
      ]},
      { id: "personalization_level", text: "How personalized is your marketing?", type: "scale", labels: ["Generic blast to all", "Basic segmentation", "Persona-based", "Behavioral triggers", "AI-personalized per individual"] },
      { id: "marketing_attribution", text: "Can you track which marketing efforts drive revenue?", type: "scale", labels: ["No tracking at all", "Basic UTM/source tracking", "Channel-level attribution", "Multi-touch attribution", "Full-funnel AI attribution"] },
    ]
  },
  {
    id: "customer_experience",
    title: "Customer Experience",
    description: "How you deliver, retain, and expand client relationships.",
    questions: [
      { id: "onboarding_quality", text: "How structured is your client onboarding?", type: "radio", options: [
        { value: "1", label: "No formal onboarding" },
        { value: "2", label: "Basic email welcome" },
        { value: "3", label: "Documented onboarding steps" },
        { value: "4", label: "Guided onboarding with milestones" },
        { value: "5", label: "Automated + personalized onboarding journey" }
      ]},
      { id: "customer_feedback", text: "How do you collect and act on customer feedback?", type: "scale", labels: ["Don't collect feedback", "Occasional surveys", "Regular surveys", "Systematic with action plans", "AI-monitored sentiment + proactive response"] },
      { id: "churn_management", text: "How well do you manage customer retention?", type: "scale", labels: ["No retention strategy", "React when they leave", "Basic check-ins", "Proactive health monitoring", "AI-predicted churn with automated interventions"] },
      { id: "upsell_process", text: "How systematic is your upsell/cross-sell process?", type: "radio", options: [
        { value: "1", label: "No upsell process" }, { value: "2", label: "Ad hoc when opportunity arises" },
        { value: "3", label: "Some triggers defined" }, { value: "4", label: "Systematic with playbooks" },
        { value: "5", label: "AI-driven recommendations and automated campaigns" }
      ]},
    ]
  },
  {
    id: "operations_admin",
    title: "Operations & Admin",
    description: "How your business runs day-to-day behind the scenes.",
    questions: [
      { id: "manual_hours", text: "How many hours per week does your team spend on repetitive manual tasks?", type: "radio", options: [
        { value: "1", label: "40+ hours/week" }, { value: "2", label: "20-40 hours/week" },
        { value: "3", label: "10-20 hours/week" }, { value: "4", label: "5-10 hours/week" },
        { value: "5", label: "Under 5 hours — most is automated" }
      ]},
      { id: "process_documentation", text: "How well documented are your core processes?", type: "scale", labels: ["Nothing documented", "Some notes exist", "Key processes documented", "Most processes documented", "Full SOPs with version control"] },
      { id: "reporting_maturity", text: "How do you generate reports and track KPIs?", type: "radio", options: [
        { value: "1", label: "Manual spreadsheets" }, { value: "2", label: "Basic dashboards" },
        { value: "3", label: "Automated reports from tools" }, { value: "4", label: "Integrated BI dashboard" },
        { value: "5", label: "AI-generated insights and anomaly detection" }
      ]},
      { id: "bottleneck_severity", text: "How much do operational bottlenecks slow your growth?", type: "scale", labels: ["Severely — can't scale", "Significantly limiting", "Moderate friction", "Minor issues", "Minimal — operations are smooth"] },
    ]
  },
  {
    id: "team_structure",
    title: "Team Structure",
    description: "Your team composition and capacity.",
    questions: [
      { id: "team_dependency", text: "How dependent is the business on specific individuals?", type: "scale", labels: ["Completely dependent on 1-2 people", "Heavily dependent", "Moderate dependency", "Some redundancy", "Fully systemized — no single point of failure"] },
      { id: "hiring_pressure", text: "How much pressure are you under to hire?", type: "radio", options: [
        { value: "1", label: "Desperately need to hire but can't afford to" },
        { value: "2", label: "Need to hire, actively looking" },
        { value: "3", label: "Could use 1-2 more people" },
        { value: "4", label: "Team is adequate" },
        { value: "5", label: "Team is strong — looking to optimize, not add" }
      ]},
      { id: "delegation_effectiveness", text: "How effectively does your team handle delegation?", type: "scale", labels: ["Everything falls back to founder", "Poor delegation", "Moderate", "Good delegation with clear ownership", "Self-managing teams with AI support"] },
    ]
  },
  {
    id: "software_stack",
    title: "Software Stack",
    description: "The tools and systems you currently use.",
    questions: [
      { id: "tool_count", text: "How many software tools does your business use daily?", type: "radio", options: [
        { value: "1", label: "1-3 tools" }, { value: "2", label: "4-7 tools" },
        { value: "3", label: "8-12 tools" }, { value: "4", label: "13-20 tools" }, { value: "5", label: "20+ tools" }
      ]},
      { id: "tool_integration", text: "How well are your tools integrated?", type: "scale", labels: ["Not at all — siloed", "Basic manual transfers", "Some integrations", "Most tools connected", "Fully integrated tech stack"] },
      { id: "crm_maturity", text: "How mature is your CRM usage?", type: "radio", options: [
        { value: "1", label: "No CRM" }, { value: "2", label: "Have one but barely use it" },
        { value: "3", label: "Actively used but messy data" }, { value: "4", label: "Well-maintained with workflows" },
        { value: "5", label: "CRM is the central nervous system" }
      ]},
      { id: "data_quality", text: "How clean and reliable is your business data?", type: "scale", labels: ["Very messy / unreliable", "Mostly outdated", "Mixed quality", "Good quality", "Clean, automated, and trusted"] },
    ]
  },
  {
    id: "automation_usage",
    title: "Current Automation",
    description: "What you've already automated — and what you haven't.",
    questions: [
      { id: "automation_level", text: "What is your current level of business automation?", type: "radio", options: [
        { value: "1", label: "Almost nothing is automated" },
        { value: "2", label: "Basic email sequences only" },
        { value: "3", label: "Some workflows automated (Zapier, etc.)" },
        { value: "4", label: "Significant automation across departments" },
        { value: "5", label: "AI-driven automation running autonomously" }
      ]},
      { id: "ai_adoption", text: "How much AI does your business currently use?", type: "radio", options: [
        { value: "1", label: "None" }, { value: "2", label: "ChatGPT for ad-hoc tasks" },
        { value: "3", label: "AI tools for specific functions (writing, design)" },
        { value: "4", label: "AI integrated into core workflows" },
        { value: "5", label: "Agentic AI systems running business processes" }
      ]},
      { id: "automation_blockers", text: "What's the biggest blocker to more automation?", type: "radio", options: [
        { value: "1", label: "Don't know where to start" },
        { value: "2", label: "No technical resources" },
        { value: "3", label: "Messy data / no documentation" },
        { value: "4", label: "Budget constraints" },
        { value: "5", label: "Team resistance to change" }
      ]},
    ]
  },
  {
    id: "growth_constraints",
    title: "Growth Constraints",
    description: "What's holding your business back from the next level.",
    questions: [
      { id: "primary_constraint", text: "What is the single biggest constraint on your growth right now?", type: "radio", options: [
        { value: "1", label: "Not enough leads / pipeline" },
        { value: "2", label: "Can't close enough deals" },
        { value: "3", label: "Operational bottlenecks — can't deliver at scale" },
        { value: "4", label: "Team capacity — people are maxed out" },
        { value: "5", label: "Cash flow / capital constraints" }
      ]},
      { id: "scaling_readiness", text: "If you 2x'd your revenue tomorrow, could your operations handle it?", type: "radio", options: [
        { value: "1", label: "No — everything would break" },
        { value: "2", label: "Probably not — major gaps" },
        { value: "3", label: "Partially — some areas would struggle" },
        { value: "4", label: "Mostly yes — a few adjustments needed" },
        { value: "5", label: "Absolutely — systems are built for scale" }
      ]},
      { id: "competitive_pressure", text: "How much competitive pressure do you face?", type: "scale", labels: ["Minimal", "Low", "Moderate", "High", "Intense — competitors are outpacing us"] },
    ]
  },
  {
    id: "pain_points",
    title: "Operational Pain Points",
    description: "Where the friction lives in your day-to-day.",
    questions: [
      { id: "biggest_time_waste", text: "Where does your team waste the most time?", type: "radio", options: [
        { value: "1", label: "Data entry and admin work" },
        { value: "2", label: "Searching for information / context switching" },
        { value: "3", label: "Meetings and internal coordination" },
        { value: "4", label: "Manual reporting and updates" },
        { value: "5", label: "Fixing errors from broken processes" }
      ]},
      { id: "customer_friction", text: "Where do customers experience the most friction?", type: "radio", options: [
        { value: "1", label: "Initial response / first contact" },
        { value: "2", label: "Onboarding process" },
        { value: "3", label: "Ongoing communication / updates" },
        { value: "4", label: "Billing and payments" },
        { value: "5", label: "Getting support when needed" }
      ]},
      { id: "process_repetition", text: "How much repetitive work happens across your team daily?", type: "scale", labels: ["Extreme — most work is repetitive", "High", "Moderate", "Low", "Minimal — most repetition is automated"] },
    ]
  },
  {
    id: "ai_readiness",
    title: "AI Readiness",
    description: "How prepared your organization is for AI transformation.",
    questions: [
      { id: "leadership_buy_in", text: "How bought-in is leadership on AI adoption?", type: "scale", labels: ["Skeptical / resistant", "Curious but cautious", "Open to exploring", "Actively investing", "AI-first mindset"] },
      { id: "budget_readiness", text: "Do you have budget allocated for AI / automation initiatives?", type: "radio", options: [
        { value: "1", label: "No budget" }, { value: "2", label: "Would need to find budget" },
        { value: "3", label: "Small budget available" }, { value: "4", label: "Meaningful budget allocated" },
        { value: "5", label: "Significant investment planned" }
      ]},
      { id: "implementation_timeline", text: "How urgently do you want to implement AI systems?", type: "radio", options: [
        { value: "1", label: "Just exploring — no timeline" },
        { value: "2", label: "Within 6-12 months" },
        { value: "3", label: "Within 3-6 months" },
        { value: "4", label: "Within 1-3 months" },
        { value: "5", label: "Immediately — ready to start now" }
      ]},
      { id: "change_readiness", text: "How adaptable is your team to new tools and processes?", type: "scale", labels: ["Very resistant", "Slow to adopt", "Average", "Adaptable", "Early adopters — love new tools"] },
    ]
  },
];

export const TOTAL_QUESTIONS = DISCOVERY_SECTIONS.reduce((sum, s) => sum + s.questions.length, 0);

export function getVisibleQuestions(section, answers) {
  return section.questions.filter(q => {
    if (!q.showIf) return true;
    const depVal = answers[section.id]?.[q.showIf.questionId];
    if (depVal === undefined) return false;
    const numVal = typeof depVal === "number" ? depVal : parseInt(depVal, 10);
    if (q.showIf.condition === "lte") return numVal <= q.showIf.value;
    if (q.showIf.condition === "gte") return numVal >= q.showIf.value;
    if (q.showIf.condition === "eq") return numVal === q.showIf.value;
    return true;
  });
}

// Backward compat — map old name
export const AUDIT_SECTIONS = DISCOVERY_SECTIONS;
